import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, ChevronLeft, ChevronRight, Lock, Milestone, Pencil, Send, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { EmptyState } from '@/components/ui/empty-state'
import { EtainablSpinner } from '@/components/etainabl-spinner'
import { MessageThread } from '@/features/ticket-detail/MessageThread'
import { WaitingOnPill } from './WaitingOnPill'
import { useTrackerPresentation } from '@/queries/tracker'
import { useAdminPatch, useReply, useSetTrackerStatus, useUpdateTracker, extractErrorMessage } from '@/queries/ticketMutations'
import { useIsAdmin } from '@/lib/auth'
import { formatTicketRef } from '@/lib/ticketRef'
import {
  trackerStatusLabels,
  trackerStatusVariants,
  trackerStatusOrder,
  devStageLabels,
  devStageVariants,
  priorityLabels,
  waitingOnLabel,
  waitingOnOrder,
} from '@/lib/status'
import type { TrackerPresentationEntry, TrackerStatus, TrackerWaitingOn } from '@/types/tracker'
import type { TicketPriority } from '@/types/ticket'

const INTERACTIVE_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'])

// Arrow/Escape must not hijack typing inside a form control — there are none
// on this read-only deck today, but the guard costs nothing and the moment
// one is added (or a future toast/dialog stacks a button on top) a stray
// Escape or arrow shouldn't double as both "activate this control" and
// "navigate the deck".
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return INTERACTIVE_TAGS.has(target.tagName) || target.isContentEditable
}

// Small, unobtrusive summary of the filters carried from the tracker list
// (design doc "Presenting follows the list's filters", 2026-08-24) — e.g.
// "High priority, 'EDF'" — so the presenter can see at a glance that the
// deck is filtered, without it competing with the position indicator above it.
function activeFiltersLabel(status?: TrackerStatus, priority?: TicketPriority, q?: string, waitingOn?: TrackerWaitingOn, companyName?: string): string | null {
  const parts: string[] = []
  if (priority) parts.push(`${priorityLabels[priority]} priority`)
  if (status) parts.push(trackerStatusLabels[status])
  if (waitingOn) parts.push(`Waiting on ${waitingOnLabel(waitingOn, companyName)}`)
  if (q) parts.push(`'${q}'`)
  return parts.length ? parts.join(', ') : null
}

// Full-screen, chrome-less message for the guard/empty/error states — the
// deck can't fall back on AppShell's centering, so each of these owns its
// own h-screen frame instead.
function MessageScreen({
  icon,
  title,
  body,
  onExit,
}: {
  icon: React.ReactNode
  title: string
  body: string
  onExit: () => void
}) {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background">
      <EmptyState icon={icon} title={title} body={body} />
      <Button variant="outline" onClick={onExit}>
        Exit
      </Button>
    </div>
  )
}

// Small error line next to the edited control — the deck has no Toaster (see
// module-level comment on PresentationView), so a failed edit must render
// here instead, close enough to the control that the presenter isn't left
// guessing which field the illegal-transition or validation message refers to.
function InlineError({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p role="alert" className="mt-1 text-xs text-destructive">
      {message}
    </p>
  )
}

type EditableField = 'priority' | 'status' | 'subject' | 'impact' | 'nextAction' | 'waitingOn' | null

// Click-to-edit card for a client call in progress (design doc "Presenting is
// also editing", added 2026-08-24): priority/tracker-status become a select,
// subject/impact become a text field, all through the same admin mutations
// the tracker detail panel uses. Every commit path funnels through
// PresentationView's single refetch — see the module-level comment above —
// so this component never talks to the server except via those mutations.
function PresentationCard({ entry, companyName }: { entry: TrackerPresentationEntry; companyName: string }) {
  const { tracker } = entry
  const ticketId = entry._id

  const patchTicket = useAdminPatch(ticketId)
  const setStatus = useSetTrackerStatus(ticketId)
  const updateTracker = useUpdateTracker(ticketId)
  const reply = useReply(ticketId)

  const [editing, setEditing] = useState<EditableField>(null)
  const [errors, setErrors] = useState<{
    priority?: string
    status?: string
    subject?: string
    impact?: string
    nextAction?: string
    comment?: string
  }>({})
  const [comment, setComment] = useState('')

  // Escape cancels an inline text edit without committing it. Blur normally
  // commits (see handleSubjectBlur/handleImpactBlur), but Escape already
  // closes the field itself, and a browser can still fire blur as the input
  // is removed from the DOM — these refs suppress that one blur so Escape
  // never commits the value it just cancelled.
  const skipSubjectBlurRef = useRef(false)
  const skipImpactBlurRef = useRef(false)
  const skipNextActionBlurRef = useRef(false)

  async function changePriority(priority: TicketPriority) {
    setEditing(null)
    setErrors(prev => ({ ...prev, priority: undefined }))
    try {
      await patchTicket.mutateAsync({ priority })
    } catch (err) {
      setErrors(prev => ({ ...prev, priority: extractErrorMessage(err, 'Could not update priority') }))
    }
  }

  async function changeStatus(status: TrackerStatus) {
    setEditing(null)
    setErrors(prev => ({ ...prev, status: undefined }))
    try {
      await setStatus.mutateAsync({ status })
    } catch (err) {
      setErrors(prev => ({ ...prev, status: extractErrorMessage(err, 'Could not update status') }))
    }
  }

  async function commitSubject(value: string) {
    setEditing(null)
    const subject = value.trim()
    if (!subject || subject === entry.subject) return
    setErrors(prev => ({ ...prev, subject: undefined }))
    try {
      await patchTicket.mutateAsync({ subject })
    } catch (err) {
      setErrors(prev => ({ ...prev, subject: extractErrorMessage(err, 'Could not update subject') }))
    }
  }

  async function commitImpact(value: string) {
    setEditing(null)
    const impact = value.trim()
    if (impact === (tracker.impact ?? '')) return
    setErrors(prev => ({ ...prev, impact: undefined }))
    try {
      await updateTracker.mutateAsync({ impact })
    } catch (err) {
      setErrors(prev => ({ ...prev, impact: extractErrorMessage(err, 'Could not update impact') }))
    }
  }

  // Next action and waiting-on share one error line: they're one idea on the card.
  async function commitNextAction(input: { nextAction?: string; waitingOn?: TrackerWaitingOn | null }) {
    setEditing(null)
    if (input.nextAction !== undefined && input.nextAction === (tracker.nextAction ?? '')) return
    if (input.waitingOn !== undefined && input.waitingOn === (tracker.waitingOn ?? null)) return
    setErrors(prev => ({ ...prev, nextAction: undefined }))
    try {
      await updateTracker.mutateAsync(input)
    } catch (err) {
      const fallback = input.waitingOn !== undefined ? 'Could not update who it is waiting on' : 'Could not update the next action'
      setErrors(prev => ({ ...prev, nextAction: extractErrorMessage(err, fallback) }))
    }
  }

  function handleNextActionKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault()
      skipNextActionBlurRef.current = true
      setEditing(null)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.blur()
    }
  }

  function handleNextActionBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (skipNextActionBlurRef.current) {
      skipNextActionBlurRef.current = false
      return
    }
    commitNextAction({ nextAction: e.target.value.trim() })
  }

  // Posts a public reply — it emails the ticket creator exactly like any
  // other reply (see supportTicket.service#replyToTicket). The call does not
  // suppress that notification: "Replies ... email exactly as they do
  // everywhere else" (design doc "Presenting is also editing").
  async function postComment() {
    const body = comment.trim()
    if (!body) return
    setErrors(prev => ({ ...prev, comment: undefined }))
    try {
      await reply.mutateAsync({ body })
      setComment('')
    } catch (err) {
      setErrors(prev => ({ ...prev, comment: extractErrorMessage(err, 'Could not post comment') }))
    }
  }

  function handleSubjectKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault()
      skipSubjectBlurRef.current = true
      setEditing(null)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      e.currentTarget.blur()
    }
  }

  function handleSubjectBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (skipSubjectBlurRef.current) {
      skipSubjectBlurRef.current = false
      return
    }
    commitSubject(e.target.value)
  }

  function handleImpactKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Escape') {
      e.preventDefault()
      skipImpactBlurRef.current = true
      setEditing(null)
    }
  }

  function handleImpactBlur(e: React.FocusEvent<HTMLTextAreaElement>) {
    if (skipImpactBlurRef.current) {
      skipImpactBlurRef.current = false
      return
    }
    commitImpact(e.target.value)
  }

  return (
    <div className="space-y-6">
      <div>
        <span className="text-xs text-muted-foreground tabular-nums">{formatTicketRef(entry)}</span>
        {editing === 'subject' ? (
          <Input
            aria-label="Subject"
            autoFocus
            defaultValue={entry.subject}
            disabled={patchTicket.isPending}
            onKeyDown={handleSubjectKeyDown}
            onBlur={handleSubjectBlur}
            className="h-auto py-1 text-2xl font-semibold"
          />
        ) : (
          <button
            type="button"
            aria-label="Edit subject"
            onClick={() => setEditing('subject')}
            className="group flex w-full items-center gap-2 text-left"
          >
            <h2 className="text-2xl font-semibold text-foreground">{entry.subject}</h2>
            <Pencil className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" aria-hidden />
          </button>
        )}
        <InlineError message={errors.subject} />
      </div>

      {/* Category/dev-stage stay display-only — not in the design doc's
          click-to-edit list. No raw linearIssueState (arbitrary,
          engineer-edited workspace text) and no clickable Linear link either:
          this is a screen-shared deck, not a one-click hop into the internal
          workspace mid-call. All three stay on the detail view. */}
      <div className="flex flex-wrap items-start gap-2">
        <div>
          {editing === 'status' ? (
            <select
              aria-label="Tracker status"
              autoFocus
              defaultValue={tracker.status}
              disabled={setStatus.isPending}
              onChange={e => changeStatus(e.target.value as TrackerStatus)}
              onBlur={() => setEditing(null)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditing(null)
                }
              }}
              className="border rounded px-2 py-1 text-sm"
            >
              {trackerStatusOrder.map(s => (
                <option key={s} value={s}>
                  {trackerStatusLabels[s]}
                </option>
              ))}
            </select>
          ) : (
            <Badge
              variant={trackerStatusVariants[tracker.status]}
              role="button"
              tabIndex={0}
              aria-label="Edit tracker status"
              onClick={() => setEditing('status')}
              onKeyDown={e => {
                if (e.key === 'Enter') setEditing('status')
              }}
              className="cursor-pointer"
            >
              {trackerStatusLabels[tracker.status]}
            </Badge>
          )}
          <InlineError message={errors.status} />
        </div>

        <div>
          {editing === 'priority' ? (
            <select
              aria-label="Priority"
              autoFocus
              defaultValue={entry.priority}
              disabled={patchTicket.isPending}
              onChange={e => changePriority(e.target.value as TicketPriority)}
              onBlur={() => setEditing(null)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditing(null)
                }
              }}
              className="border rounded px-2 py-1 text-sm"
            >
              {(['low', 'normal', 'high'] as const).map(p => (
                <option key={p} value={p}>
                  {priorityLabels[p]}
                </option>
              ))}
            </select>
          ) : (
            <Badge
              variant="outline"
              role="button"
              tabIndex={0}
              aria-label="Edit priority"
              onClick={() => setEditing('priority')}
              onKeyDown={e => {
                if (e.key === 'Enter') setEditing('priority')
              }}
              className="cursor-pointer"
            >
              {priorityLabels[entry.priority]} priority
            </Badge>
          )}
          <InlineError message={errors.priority} />
        </div>

        {tracker.category && <Badge variant="outline">{tracker.category}</Badge>}
        <Badge variant={devStageVariants[tracker.devStage]}>{devStageLabels[tracker.devStage]}</Badge>
      </div>

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Impact</h3>
        {editing === 'impact' ? (
          <Textarea
            aria-label="Impact"
            autoFocus
            defaultValue={tracker.impact ?? ''}
            rows={2}
            className="text-sm"
            disabled={updateTracker.isPending}
            onKeyDown={handleImpactKeyDown}
            onBlur={handleImpactBlur}
          />
        ) : (
          <button type="button" aria-label="Edit impact" onClick={() => setEditing('impact')} className="block w-full text-left">
            <p className="text-sm text-foreground">
              {tracker.impact || <span className="italic text-muted-foreground">Add impact…</span>}
            </p>
          </button>
        )}
        <InlineError message={errors.impact} />
      </div>

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Next action</h3>
        {editing === 'nextAction' ? (
          <Input
            aria-label="Next action"
            autoFocus
            maxLength={500}
            defaultValue={tracker.nextAction ?? ''}
            className="text-sm"
            disabled={updateTracker.isPending}
            onKeyDown={handleNextActionKeyDown}
            onBlur={handleNextActionBlur}
          />
        ) : (
          <button type="button" aria-label="Edit next action" onClick={() => setEditing('nextAction')} className="block w-full text-left">
            <p className="text-sm text-foreground">
              {tracker.nextAction || <span className="italic text-muted-foreground">Add next action…</span>}
            </p>
          </button>
        )}
        <div className="mt-1.5">
          {editing === 'waitingOn' ? (
            <select
              aria-label="Waiting on"
              autoFocus
              defaultValue={tracker.waitingOn ?? ''}
              disabled={updateTracker.isPending}
              onChange={e => commitNextAction({ waitingOn: (e.target.value || null) as TrackerWaitingOn | null })}
              onBlur={() => setEditing(null)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  setEditing(null)
                }
              }}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="">Nobody</option>
              {waitingOnOrder.map(w => (
                <option key={w} value={w}>
                  {waitingOnLabel(w, companyName)}
                </option>
              ))}
            </select>
          ) : (
            <button type="button" aria-label="Edit waiting on" onClick={() => setEditing('waitingOn')} className="text-left">
              {tracker.waitingOn ? (
                <WaitingOnPill waitingOn={tracker.waitingOn} companyName={companyName} />
              ) : (
                <span className="text-xs italic text-muted-foreground">Waiting on nobody</span>
              )}
            </button>
          )}
        </div>
        <InlineError message={errors.nextAction} />
      </div>

      {entry.recentMessages.length > 0 && (
        <div data-testid="recent-activity">
          <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Recent activity</h3>
          {/* The same conversation the ticket page and the tracker detail
              render — read-only here, since the deck's only write path into a
              thread is the Quick comment box below. The opening message stays
              unrewritable (design doc "Presenting is also editing"): the
              thread has no editing affordance at all. */}
          <MessageThread ticketId={entry._id} messages={entry.recentMessages} />
        </div>
      )}

      <div>
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">Quick comment</h3>
        <div className="flex gap-2">
          <Input
            aria-label="Quick comment"
            value={comment}
            disabled={reply.isPending}
            onChange={e => setComment(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                postComment()
              }
            }}
            placeholder="Add a comment the client will see…"
          />
          <Button type="button" size="sm" disabled={reply.isPending || !comment.trim()} onClick={postComment}>
            <Send className="h-4 w-4" />
            Post
          </Button>
        </div>
        <InlineError message={errors.comment} />
      </div>
    </div>
  )
}

// Full-screen deck for a client call: the sidebar/nav/view-as controls are
// structurally absent while this route is mounted (see __root.tsx), and this
// component itself makes exactly one request — GET .../tracker/presentation —
// and renders only what it returned. It must never reach for ticket detail or
// a message thread of its own: that per-card fetch is exactly how the
// predecessor app leaked an internal note onto a shared screen.
export function PresentationView({
  companyId,
  asUser,
  status,
  priority,
  q,
  waitingOn,
}: {
  companyId?: string
  asUser?: string
  // Carried from the tracker list's active filters (design doc "Presenting
  // follows the list's filters", 2026-08-24) — applied server-side by
  // getTrackerPresentation itself, not filtered again here.
  status?: string
  priority?: string
  q?: string
  waitingOn?: string
}) {
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const [includeClosed, setIncludeClosed] = useState(false)
  // Tracked by ticket id, not array position: a background refetch
  // (refetchOnWindowFocus) can reorder the list mid-call, and an index would
  // silently swap the ticket under the presenter's feet. findIndex below
  // re-locates the same id after any reorder; -1 (id no longer present, or
  // no id chosen yet) falls back to the first card.
  const [currentId, setCurrentId] = useState<string | undefined>(undefined)

  // Present is staff-only — clients don't present. Fold that into the query
  // itself (not just the render below) so a client who lands on this URL
  // triggers no request at all, not even one the server would just ignore.
  const { data, isLoading, error } = useTrackerPresentation(
    isAdmin ? companyId : undefined,
    { includeClosed, status, priority, q, waitingOn },
    isAdmin ? asUser : undefined,
  )

  const tickets = data?.data ?? []
  const total = data?.total ?? tickets.length
  const rawIndex = tickets.findIndex(t => t._id === currentId)
  const clampedIndex = rawIndex === -1 ? 0 : rawIndex
  const current = tickets[clampedIndex]
  const filtersLabel = activeFiltersLabel(
    status as TrackerStatus | undefined,
    priority as TicketPriority | undefined,
    q,
    waitingOn as TrackerWaitingOn | undefined,
    data?.companyName,
  )

  const exit = () => navigate({ to: '/tracker', search: { asUser } as never })

  function goTo(nextIndex: number) {
    const clamped = Math.max(0, Math.min(nextIndex, tickets.length - 1))
    const target = tickets[clamped]
    if (target) setCurrentId(target._id)
  }

  // Toggling the filter changes which tickets exist at all — never leave the
  // deck parked on an id the new page doesn't have; falls back to the first
  // card via the -1 branch above. Same for the carried list filters: they're
  // fixed for the lifetime of this route (set once by the Present link), but
  // included for completeness if a future revision makes them editable in-deck.
  useEffect(() => {
    setCurrentId(undefined)
  }, [includeClosed, status, priority, q])

  // A toast already on screen when the presenter navigates here (e.g. they
  // just edited a tracker field) would otherwise ride along — see __root.tsx
  // for why dropping <Toaster/> from this branch isn't sufficient on its own
  // for every path. Belt and braces: clear the queue the moment the deck
  // mounts too.
  useEffect(() => {
    toast.dismiss()
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isInteractiveTarget(e.target)) return
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goTo(clampedIndex + 1)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goTo(clampedIndex - 1)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        exit()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, clampedIndex, asUser])

  // Staff-only, checked here (not just by whoever links to this route) so
  // presenting is verifiably unreachable for a client regardless of how they
  // arrived — mirrors TrackerControls/TrackerListView's isAdmin gate.
  if (!isAdmin) {
    return (
      <MessageScreen
        icon={<Lock />}
        title="Presentation is available to Etainabl staff"
        body="This view is for staff to walk a client through their tracker on a call."
        onExit={exit}
      />
    )
  }

  // Reachable only for an admin: arrived at /tracker/present with neither
  // companyId nor an asUser lens, so the query never ran (see `enabled`
  // above) — a distinct, honest message rather than the generic "couldn't
  // load" below, which implies a request was attempted and failed.
  if (!companyId && !asUser) {
    return (
      <MessageScreen
        icon={<Milestone />}
        title="Nothing selected to present"
        body="Open Present from a specific company's row on the tracker list."
        onExit={exit}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <EtainablSpinner size="lg" srLabel="Loading presentation" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <MessageScreen
        icon={<AlertTriangle />}
        title="Couldn't load the tracker"
        body="Something went wrong loading the presentation."
        onExit={exit}
      />
    )
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      <header className="flex items-center justify-between border-b border-border px-6 py-4">
        <div className="flex items-start gap-3 min-w-0">
          <h1 className="truncate text-lg font-semibold text-foreground">{data.companyName}</h1>
          <div className="flex shrink-0 flex-col">
            <span className="text-sm text-muted-foreground tabular-nums">
              {tickets.length === 0 ? '0 of 0' : `${clampedIndex + 1} of ${total}`}
            </span>
            {/* Unobtrusive — a small line under the position indicator, not a
                banner — so the presenter knows the deck is filtered without it
                competing with the company name/position for attention. */}
            {filtersLabel && <span className="text-xs text-muted-foreground">{filtersLabel}</span>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={e => setIncludeClosed(e.target.checked)}
            />
            Include closed
          </label>
          <Button variant="outline" size="sm" onClick={exit}>
            <X className="mr-1.5 h-4 w-4" />
            Exit
          </Button>
        </div>
      </header>

      {tickets.length === 0 || !current ? (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState
            icon={<Milestone />}
            title="Nothing to present"
            body="No tracked issues to present yet."
          />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-between gap-6 px-10 py-8">
          <Button
            variant="ghost"
            size="icon-lg"
            aria-label="Previous"
            disabled={clampedIndex === 0}
            onClick={() => goTo(clampedIndex - 1)}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <div className="min-w-0 max-w-3xl flex-1 overflow-y-auto">
            {/* Keyed by id, not position: forces a remount (and so a reset of
                any in-progress inline edit) when the presenter moves to a
                genuinely different ticket, while a same-ticket refetch after
                a successful edit — same id, new data — updates in place. */}
            <PresentationCard key={current._id} entry={current} companyName={data.companyName} />
          </div>

          <Button
            variant="ghost"
            size="icon-lg"
            aria-label="Next"
            disabled={clampedIndex >= tickets.length - 1}
            onClick={() => goTo(clampedIndex + 1)}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>
      )}
    </div>
  )
}
